/** Closed, windowless corridor personnel door with the leaf rigged for a later opening system. */
import * as THREE from 'three';
import { surface } from '../materials';
import { seatOnOrigin } from '../origin';
import type { AssetDefinition } from '../types';

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'personnel-door';
  const frameSteel = surface('metal', 0x5e6b6c);
  const leafSteel = surface('metal', 0x758182);
  const brightSteel = surface('metal', 0xaeb6b5);

  for (const [x, y, width, height, name] of [
    [-0.48, 1.085, 0.06, 2.17, 'frame-left'], [0.48, 1.085, 0.06, 2.17, 'frame-right'],
    [0, 2.14, 1.02, 0.06, 'frame-head'],
  ] as const) {
    const frame = new THREE.Mesh(new THREE.BoxGeometry(width, height, 0.12), frameSteel);
    frame.name = name;
    frame.position.set(x, y, 0);
    frame.castShadow = true;
    group.add(frame);
  }

  // The pivot lives on the left hinge edge rather than at the leaf centre; a
  // later swing can rotate this one node without first compensating the leaf.
  const hinge = new THREE.Group();
  hinge.name = 'door-hinge';
  hinge.position.set(-0.45, 0, 0);
  const leaf = new THREE.Group();
  leaf.name = 'door-leaf';
  hinge.add(leaf);
  group.add(hinge);

  const panel = new THREE.Mesh(new THREE.BoxGeometry(0.9, 2.05, 0.055), leafSteel);
  panel.name = 'steel-leaf-panel';
  panel.position.set(0.45, 1.025, 0);
  panel.castShadow = true;
  leaf.add(panel);

  const kickPlate = new THREE.Mesh(new THREE.BoxGeometry(0.76, 0.22, 0.012), brightSteel);
  kickPlate.name = 'kick-plate';
  kickPlate.position.set(0.45, 0.14, 0.034);
  leaf.add(kickPlate);

  for (const [offset, index] of [[0.25, 1], [0.55, 2], [0.85, 3]] as const) {
    const pin = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.045, 8), brightSteel);
    pin.name = `leaf-hinge-${index}`;
    pin.rotation.x = Math.PI / 2;
    pin.position.set(0.012, offset, 0.022);
    leaf.add(pin);
  }

  for (const offset of [0.27, 0.62]) {
    const rib = new THREE.Mesh(new THREE.BoxGeometry(0.018, 1.35, 0.012), brightSteel);
    rib.name = `pressed-vertical-rib-${offset.toFixed(2)}`;
    rib.position.set(offset, 1.11, 0.034);
    leaf.add(rib);
  }

  const pushBar = new THREE.Mesh(new THREE.BoxGeometry(0.56, 0.055, 0.028), brightSteel);
  pushBar.name = 'horizontal-push-bar';
  pushBar.position.set(0.46, 1.05, 0.066);
  pushBar.castShadow = true;
  leaf.add(pushBar);

  // Authored from a convenient face outward, then seated on the contract's
  // origin. See `seatOnOrigin` for why five assets in this batch needed it.
  seatOnOrigin(group);

  return group;
}

export const personnelDoorAsset: AssetDefinition = {
  id: 'personnel-door',
  label: 'Personnel door',
  description: 'Closed 0.9 m windowless steel personnel door with push bar, hinges, kick plate, and a hinge-edge leaf pivot.',
  footprint: { width: 1.02, height: 2.17, length: 0.14 },
  create: build,
};
